import { useState, useEffect } from "react";
import { Box, Flex, Button, Spinner } from "@chakra-ui/react";
import { Trans, useTranslation } from "react-i18next";
import environment, { getAppValue } from "src/utils/environment";
import { getLocale } from "src/utils/locale";
import openExternal from "src/utils/openExternal";
import { H2, Subtitle, Caption } from "src/atoms/typography";
import CloseButton from "src/atoms/CloseButton";
import Modal from "src/components/Modal";
import SvgContainer from "src/components/SvgContainer";
import BackButton from "src/components/BackButton";
import { freemiumFeaturesWithIcons } from "src/modules/purchase/components/freemiumFeatures";
import ProductButton from "./ProductButton";
import BuyMenu from "./BuyMenu";
import { Author, Review } from "./UserReview";

import { ReactComponent as BgIcon } from "./icons/bg-top.svg";
import { ReactComponent as PeopleIcon } from "./icons/people.svg";
import { ReactComponent as PeopleDogIcon } from "./icons/people-dog.svg";
import { ReactComponent as ReviewStarsIcon } from "./icons/review-stars.svg";
import { ReactComponent as CloseIcon } from "./icons/close.svg";
import Blur from "src/components/Blur";

const { isBarkio } = environment;

export default function View({
  products,
  onClose,
  onCheckoutOpen,
  onBack,
  openRestore,
  isActivatingSubscription,
  closePaddleCheckout,
  isPaddleOpen,
  isPaddleLoaded,
  loadFreemiumTrial
}) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { t } = useTranslation();
  const heading = loadFreemiumTrial
    ? "premium.access.trial.heading"
    : "premium.access.heading";
  const button =
    loadFreemiumTrial && selectedProduct?.type === "yearly"
      ? "premium.tryForFree"
      : "premium.subscribe";

  useEffect(() => {
    if (!selectedProduct && products) {
      setSelectedProduct({ ...products.yearly, type: "yearly" });
    }
  }, [products, selectedProduct]);

  const primaryBg = getAppValue({
    bibino: "#8f6df5",
    barkio: "#58a509"
  });
  const secondaryBg = getAppValue({
    bibino: "#704dd9",
    barkio: "#3b8500"
  });

  const PersonIcon = getAppValue({
    bibino: PeopleIcon,
    barkio: PeopleDogIcon
  });

  return (
    <>
      {isPaddleOpen && (
        <Modal
          isOpen={isPaddleOpen}
          bg="transparent"
          zIndex="1500"
          bodyProps={{ bg: "transparent", boxShadow: "none" }}
        >
          {!isPaddleLoaded && <Spinner size="xl" />}
          <Box
            p="3px"
            bg="white"
            onClick={closePaddleCheckout}
            borderRadius="100%"
            position="fixed"
            top="18px"
            right="24px"
            cursor="pointer"
          >
            <SvgContainer icon={CloseIcon} fill="black54" size="20px" />
          </Box>
        </Modal>
      )}
      {onBack && <BackButton absolute onClick={onBack} fill="white" />}
      <Blur show={Boolean(!products)} />
      <Flex position="absolute" top="14px" right="20px" zIndex={100}>
        <BuyMenu
          isDisabled={isActivatingSubscription || Boolean(!products)}
          openRestore={openRestore}
          fill="mediumEmphasis"
        />
        <CloseButton
          isDisabled={isActivatingSubscription}
          fill="mediumEmphasis"
          onClick={onClose}
          ml="8px"
        />
      </Flex>
      <Box width="100%" height="100%">
        <Flex height="100%" zIndex={0} pos="relative">
          <Flex
            zIndex={0}
            bg={secondaryBg}
            borderLeftRadius="12px"
            width="358px"
            flexDirection="column"
            alignItems="center"
            overflowY="scroll"
            sx={{
              "::-webkit-scrollbar": {
                width: "6px"
              },
              "::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255, 255, 255, 0.4)",
                borderRight: "2px solid rgba(0, 0, 0, 0)",
                backgroundClip: "padding-box",
                borderBottomRightRadius: "3px",
                borderRopRightRadius: "3px",
                borderBottomLeftRadius: "2px",
                borderTopLeftRadius: "2px"
              }
            }}
          >
            <Flex
              px="30px"
              width="100%"
              bg={secondaryBg}
              flexDirection="column"
              alignItems="center"
            >
              <H2 textAlign="center" color="white" mt="48px" mb="24px">
                {t(heading)}
              </H2>
              <Box>
                {freemiumFeaturesWithIcons.map(
                  ({ string, Icon, iconColor }, i) => {
                    return (
                      <Flex alignItems="center" mb="16px" color="white" key={i}>
                        <Box
                          as="span"
                          mr="12px"
                          sx={{
                            svg: {
                              height: "32px",
                              width: "32px",
                              g: {
                                ...(isBarkio && { filter: "none" }),
                                rect: {
                                  fill: iconColor
                                }
                              }
                            }
                          }}
                        >
                          <Icon />
                        </Box>
                        <Subtitle color="white70">{t(string)}</Subtitle>
                      </Flex>
                    );
                  }
                )}
              </Box>
            </Flex>
            <Box width="100%" bg={secondaryBg}>
              <Box width="100%">
                <BgIcon fill={primaryBg} width="100%" />
                <Flex
                  bg={primaryBg}
                  width="100%"
                  flexDirection="column"
                  alignItems="center"
                  px="30px"
                >
                  <PersonIcon />
                  <H2 mt="12px" mb="24px" textAlign="center" color="white">
                    {t("premium.usedByThousands")}
                  </H2>
                  <ReviewStarsIcon />
                  <Review my="8px" t={t} string="premium.review.amazing" />
                  <Author string="premium.review.author1" t={t} mb="24px" />
                  <ReviewStarsIcon />
                  <Review my="8px" t={t} string="premium.review.awesome" />
                  <Author string="premium.review.author2" t={t} mb="24px" />
                  <ReviewStarsIcon />
                  <Review my="8px" t={t} string="premium.review.useful" />
                  <Author string="premium.review.author3" t={t} mb="16px" />
                </Flex>
              </Box>
            </Box>
          </Flex>
          <Flex
            width="294px"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            zIndex={0}
            // visibility={products ? "visible" : "hidden"}
          >
            <H2 mb="22px" color="highEmphasis" textAlign="center">
              {t("premium.selectPlan")}
            </H2>
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="154px"
            >
              <ProductButton
                product={products?.yearly}
                onClick={() =>
                  setSelectedProduct({
                    ...products?.yearly,
                    type: "yearly"
                  })
                }
                isSelected={
                  selectedProduct?.productId === products?.yearly.productId
                }
              />
              <ProductButton
                product={products?.monthly}
                onClick={() =>
                  setSelectedProduct({
                    ...products?.monthly,
                    type: "monthly"
                  })
                }
                isSelected={
                  selectedProduct?.productId === products?.monthly.productId
                }
              />
            </Flex>
            {isActivatingSubscription ? (
              <Spinner my="1.6rem" />
            ) : (
              <Button
                variant="containedPrimary"
                mt="12px"
                mb="8px"
                minWidth="194px"
                onClick={() =>
                  onCheckoutOpen({
                    link: selectedProduct.link,
                    productId: selectedProduct.productId
                  })
                }
              >
                {t(button)}
              </Button>
            )}
            <Box px="24px">
              <Caption
                textAlign="center"
                color="mediumEmphasis"
                sx={{
                  span: {
                    textDecoration: "underline",
                    cursor: "pointer"
                  }
                }}
              >
                <Trans i18nKey="termsAndPrivacy.short">
                  <span
                    onClick={() =>
                      openExternal({ env: "TERMS_OF_SERVICE_URL" })
                    }
                  >
                    Terms of Service
                  </span>{" "}
                  ·{" "}
                  <span
                    onClick={() =>
                      openExternal({
                        env:
                          getLocale().split("-")[0] === "de"
                            ? "GERMAN_PRIVACY_POLICY_URL"
                            : "PRIVACY_POLICY_URL"
                      })
                    }
                  >
                    Privacy Policy
                  </span>
                </Trans>
              </Caption>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </>
  );
}
